import {
  getActiveDestinations,
  hasConsentGroup,
  onConsentChanged
} from './helpers/cookieConsent';

/* Used to determine if promise is active. */
let isResolving = false;

/* Historical event storage. */
const historicalEvents = [];

/* Event storage proxy. */
export const eventsProxy = new Proxy([], {
  get(target, prop) {
    const val = target[prop];
    if (typeof val === 'function') {
      if (['push'].includes(prop)) {
        // eslint-disable-next-line func-names
        return function () {
          // eslint-disable-next-line prefer-rest-params
          const r = Array.prototype[prop].apply(target, arguments);
          // This starts our react event pipeline.
          processEvents();
          return r;
        };
      }
      return val.bind(target);
    }
    return val;
  }
});

/**
 * Function for loading analytics.js via MR.
 *
 * @param {string} writeKey
 * @param {string} analyticsJsUrl
 * @returns
 */
async function getAnalyticsJs({
  writeKey,
  analyticsJsUrl,
  gtmId,
  rudderstackWriteKey,
  rudderstackDataPlaneURL
}) {
  if (hasAnalyticsJs() || isResolving) return true;
  isResolving = true;
  const isSecuritiEnabled = window.s__c;
  await Promise.all([
    new Promise((resolve, reject) => {
      if (rudderstackWriteKey) {
        // Do Rudderstack snippet.
        /*eslint-disable */
        window.analytics = window.analytics || [];
        !(function (writeKey, dataPlaneURL) {
          'use strict';
          window.RudderSnippetVersion = '3.0.60';
          var e = 'rudderanalytics';
          window[e] || (window[e] = []);
          var rudderanalytics = window[e];
          if (Array.isArray(rudderanalytics)) {
            if (
              true === rudderanalytics.snippetExecuted &&
              window.console &&
              console.error
            ) {
              console.error(
                'RudderStack JavaScript SDK snippet included more than once.'
              );
            } else {
              (rudderanalytics.snippetExecuted = true),
                (window.rudderAnalyticsBuildType = 'legacy');
              var sdkBaseUrl = 'https://cdn.rudderlabs.com';
              var sdkVersion = 'v3';
              var sdkFileName = 'rsa.min.js';
              var scriptLoadingMode = 'async';
              var r = [
                'setDefaultInstanceKey',
                'load',
                'ready',
                'page',
                'track',
                'identify',
                'alias',
                'group',
                'reset',
                'setAnonymousId',
                'startSession',
                'endSession',
                'consent'
              ];
              for (var n = 0; n < r.length; n++) {
                var t = r[n];
                rudderanalytics[t] = (function (r) {
                  return function () {
                    var n;
                    Array.isArray(window[e])
                      ? rudderanalytics.push(
                          [r].concat(Array.prototype.slice.call(arguments))
                        )
                      : null === (n = window[e][r]) ||
                        void 0 === n ||
                        n.apply(window[e], arguments);
                  };
                })(t);
              }
              try {
                new Function(
                  'class Test{field=()=>{};test({prop=[]}={}){return prop?(prop?.property??[...prop]):import("");}}'
                ),
                  (window.rudderAnalyticsBuildType = 'modern');
              } catch (i) {}
              var d = document.head || document.getElementsByTagName('head')[0];
              var o = document.body || document.getElementsByTagName('body')[0];
              (window.rudderAnalyticsAddScript = function (e, r, n) {
                var t = document.createElement('script');
                // CUSTOM ON LOAD
                t.onload = function () {
                  resolve(true);
                };
                t.onerror = function () {
                  reject(false);
                };
                // END CUSTOM ON LOAD
                (t.src = e),
                  t.setAttribute('data-loader', 'RS_JS_SDK'),
                  r && n && t.setAttribute(r, n),
                  'async' === scriptLoadingMode
                    ? (t.async = true)
                    : 'defer' === scriptLoadingMode && (t.defer = true),
                  d
                    ? d.insertBefore(t, d.firstChild)
                    : o.insertBefore(t, o.firstChild);
              }),
                (window.rudderAnalyticsMount = function () {
                  !(function () {
                    if ('undefined' == typeof globalThis) {
                      var e;
                      var r = (function getGlobal() {
                        return 'undefined' != typeof self
                          ? self
                          : 'undefined' != typeof window
                          ? window
                          : null;
                      })();
                      r &&
                        Object.defineProperty(r, 'globalThis', {
                          value: r,
                          configurable: true
                        });
                    }
                  })(),
                    window.rudderAnalyticsAddScript(
                      ''
                        .concat(sdkBaseUrl, '/')
                        .concat(sdkVersion, '/')
                        .concat(window.rudderAnalyticsBuildType, '/')
                        .concat(sdkFileName),
                      'data-rsa-write-key',
                      writeKey
                    );
                }),
                'undefined' == typeof Promise ||
                'undefined' == typeof globalThis
                  ? window.rudderAnalyticsAddScript(
                      'https://polyfill-fastly.io/v3/polyfill.min.js?version=3.111.0&features=Symbol%2CPromise&callback=rudderAnalyticsMount'
                    )
                  : window.rudderAnalyticsMount();
              var loadOptions = {
                anonymousIdOptions: {
                  autoCapture: {
                    enabled: true,
                    source: 'segment'
                  }
                },
                consentManagement: {
                  enabled: true,
                  provider: 'custom'
                },
                preConsent: {
                  enabled: true,
                  storage: {
                    strategy: 'session'
                  },
                  events: {
                    delivery: 'buffer'
                  }
                }
              };
              // Proxy rudderanalytics to window.analytics
              window.analytics = rudderanalytics;
              rudderanalytics.load(
                writeKey,
                dataPlaneURL ||
                  'https://outsideincymbj.dataplane.rudderstack.com',
                loadOptions
              );
              // Set consent if securiti cookie is set
              // (better than checking for global securiti object which could be a race condition).
              const securitiConsent = document.cookie
                .split('; ')
                .find((row) => row.indexOf('OptOutOnRequest=') === 0)
                ?.split('OptOutOnRequest=')[1];
              if (window.analytics?.consent && securitiConsent) {
                // Set consent based on securiti cookie.
                const baseConsentObject = {
                  allowedConsentIds: ['C0001'],
                  deniedConsentIds: []
                };
                const groups = Object.fromEntries(
                  decodeURIComponent(securitiConsent)
                    .split('=')[1]
                    .split(',')
                    .map((pair) => {
                      const [key, value] = pair.split(':');
                      return [key, parseInt(value)];
                    })
                );
                // Map securiti cookie to RudderStack consent object.
                const consentObject = Object.entries(groups).reduce(
                  (acc, [key, value]) => {
                    if (value === 1 && !acc.allowedConsentIds.includes(key)) {
                      acc.allowedConsentIds.push(key);
                    } else if (
                      value === 0 &&
                      !acc.deniedConsentIds.includes(key)
                    ) {
                      acc.deniedConsentIds.push(key);
                    }
                    return acc;
                  },
                  baseConsentObject
                );
                // Set consent.
                window.analytics.consent({
                  options: {
                    trackConsent: true,
                    consentManagement: consentObject
                  }
                });
              }
            }
          }
        })(rudderstackWriteKey, rudderstackDataPlaneURL);
        /* eslint-enable */
      } else {
        // Get MetaRouter Analytics.js. (required)
        /*eslint-disable */
        var analytics = (window.analytics = window.analytics || []);
        if (!analytics.initialize)
          if (analytics.invoked)
            window.console &&
              console.error &&
              console.error('MetaRouter snippet included twice.');
          else {
            analytics.invoked = !0;
            analytics.methods = [
              'trackSubmit',
              'trackClick',
              'trackLink',
              'trackForm',
              'pageview',
              'identify',
              'reset',
              'group',
              'track',
              'ready',
              'alias',
              'page',
              'once',
              'off',
              'on'
            ];
            analytics.factory = function (t) {
              return function () {
                var e = Array.prototype.slice.call(arguments);
                e.unshift(t);
                analytics.push(e);
                return analytics;
              };
            };
            for (var t = 0; t < analytics.methods.length; t++) {
              var e = analytics.methods[t];
              analytics[e] = analytics.factory(e);
            }
            analytics.load = function (t) {
              var e = document.createElement('script');
              e.type = 'text/javascript';
              e.defer = !0;
              e.async = !0;
              e.src =
                ('https:' === document.location.protocol
                  ? 'https://'
                  : 'http://') + analyticsJsUrl;
              e.onload = function () {
                console.log('MetaRouter loaded');
                resolve(true);
              };
              e.onerror = function () {
                console.log('MetaRouter failed');
                reject(false);
              };
              var n = document.getElementsByTagName('script')[0];
              n.parentNode.insertBefore(e, n);
            };
            analytics.SNIPPET_VERSION = '3.1.0';
            analytics.load(
              writeKey,
              isSecuritiEnabled && { integrations: getActiveDestinations() }
            );
            if (isSecuritiEnabled) {
              window.addEventListener('message', (ev) => {
                if (ev.data.message === 'consent_given') {
                  onConsentChanged();
                } else if (ev.data.message === 'consolidated_consent_given') {
                  // Reload if consent has changed.
                  // Use a proxy to get around a change in Essential that fires a false positive.
                  const proxyConsent = JSON.parse(
                    JSON.stringify(window.s__c_g ? window.s__c_g : {})
                  );
                  if (!proxyConsent.Essential) proxyConsent.Essential = true;
                  if (
                    window.s__c_i_g &&
                    JSON.stringify(proxyConsent) !==
                      JSON.stringify(ev.data.consentStatuses)
                  ) {
                    location.reload();
                  }
                  onConsentChanged();
                }
              });
            }
          }
      }
      /* eslint-enable */
    }),
    new Promise((resolve, reject) => {
      // Get GTM sdk. (optional)
      const shouldLaunchGTM = isSecuritiEnabled
        ? hasConsentGroup('performance') && hasConsentGroup('targeting')
        : true;
      if (!gtmId || !shouldLaunchGTM) {
        resolve(true);
      } else {
        /*eslint-disable */
        (function (w, d, s, l, i) {
          w[l] = w[l] || [];
          w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
          var f = d.getElementsByTagName(s)[0],
            j = d.createElement(s),
            dl = l != 'dataLayer' ? '&l=' + l : '';
          j.async = true;
          j.defer = true;
          j.onload = function () {
            console.log('GTM loaded');
            resolve(true);
          };
          j.onerror = function () {
            console.log('GTM failed');
            reject(false);
          };
          j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
          f.parentNode.insertBefore(j, f);
        })(window, document, 'script', 'dataLayer', gtmId);
        /* eslint-enable */
      }
    })
  ]);
  isResolving = false;
  // eslint-disable-next-line no-console
  console.log('Analytics pipelines ready');
  processEvents();
  return true;
}

/**
 * Function for determining the status of analytics.js
 * in the browser.
 *
 * @returns {boolean}
 */
function hasAnalyticsJs() {
  return !!window.analytics;
}

/**
 * Funciton for calling our pipeline(s).
 *
 * @param {Object} Event
 * @returns {boolean}
 */
function callEvent(Event) {
  if (!hasAnalyticsJs() || !Event) return false;
  const {
    event,
    payload: { eventId, props }
  } = Event;

  try {
    // Push event into MR
    window.analytics[event].call(
      window.analytics,
      ...(eventId && [eventId]),
      props
    );
    // Push event into GTM
    if (window.dataLayer) {
      window.dataLayer.push({
        event,
        payload: {
          ...(eventId && { eventId }),
          ...(props && { props })
        }
      });
    }
    return true;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error);
    return false;
  }
}

/**
 * Function for processing our events.
 *
 * @returns {boolean}
 */
function processEvents() {
  if (!hasAnalyticsJs()) {
    const {
      options: {
        writeKey,
        analyticsJsUrl,
        gtmId,
        rudderstackWriteKey,
        rudderstackDataPlaneURL
      }
    } = eventsProxy[0];
    getAnalyticsJs({
      writeKey,
      analyticsJsUrl,
      gtmId,
      rudderstackWriteKey,
      rudderstackDataPlaneURL
    });
    // Come back later when sdk(s) are loaded.
    return false;
  }
  while (eventsProxy.length) {
    const event = eventsProxy.shift();
    if (callEvent(event)) {
      historicalEvents.push(event);
    }
  }
  return true;
}

/**
 * Function for triggering our events.
 *
 * @param {string} event
 * @param {object} payload
 * @param {object} options
 */
export const AnalyticsDefaultTrigger = (event, payload, options) => {
  eventsProxy.push({ event, payload, options });
};
